import * as React from 'react';
import { ResponsiveRadar } from '@nivo/radar';
import { useLanguage } from '../locale';
import { Config } from '../config';

type EmotionData = {
  emotion: string;
  count: number;
};

type EmotionRadarChartProps = {
  emotions: {
    anger_count: number;
    fear_count: number;
    joy_count: number;
    sadness_count: number;
    disgust_count: number;
  } | null;
  className?: string;
};

const EmotionRadarChart: React.FC<EmotionRadarChartProps> = ({
  emotions,
  className = '',
}) => {
  const [t] = useLanguage();

  const data = React.useMemo((): EmotionData[] => {
    if (!emotions) return [];

    const allEmotions = [
      {
        emotion: t('dashboard.analytics.emotions.anger'),
        count: emotions.anger_count,
      },
      {
        emotion: t('dashboard.analytics.emotions.fear'),
        count: emotions.fear_count,
      },
      {
        emotion: t('dashboard.analytics.emotions.joy'),
        count: emotions.joy_count,
      },
      {
        emotion: t('dashboard.analytics.emotions.sadness'),
        count: emotions.sadness_count,
      },
      {
        emotion: t('dashboard.analytics.emotions.disgust'),
        count: emotions.disgust_count,
      },
    ];

    // Filter out emotions with 0 count
    return allEmotions.filter(emotion => emotion.count > 0);
  }, [emotions, t]);

  // Show "no data" only if there are no emotions at all
  if (!emotions || data.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <span className="text-gray-500">{t('shared.noData')}</span>
      </div>
    );
  }

  return (
    <div className={`h-64 ${className}`}>
      <ResponsiveRadar
        data={data}
        keys={['count']}
        indexBy="emotion"
        valueFormat=">-.0f"
        margin={{ top: 40, right: 80, bottom: 40, left: 80 }}
        borderColor={{ from: 'color' }}
        gridLabelOffset={36}
        dotSize={8}
        dotColor={{ theme: 'background' }}
        dotBorderWidth={2}
        colors={[Config.primaryColor]}
        blendMode="multiply"
        motionConfig="wobbly"
      />
    </div>
  );
};

export default EmotionRadarChart;
